import { useEffect, useMemo } from "react"
import { useGetNonPoBudgetPreviewQuery } from "shared/redux/endpoints/procurementV2"
import useDebouncedValue from "../../../hooks/useDebouncedValue"
import { formatVND } from "../../../constants"

const now = new Date()
const fiscalYear = now.getFullYear()
const fiscalMonth = now.getMonth() + 1

const CONFIG_MESSAGES = {
  CENTER_NOT_FOUND: "This center was not found.",
  CENTER_UNMAPPED: "This center is not mapped to a budget center.",
  CATEGORY_NOT_FOUND: "This budget category was not found.",
  BUDGET_BUCKET_NOT_FOUND: `No active budget was found for this category in ${String(fiscalMonth).padStart(2, "0")}/${fiscalYear}.`,
}

// Preview for an unsaved Non-PO, one entry per center the lines spend from
// (BE commits each center separately on submit, into the current month).
// `pending` stays true while the debounced inputs catch up with the form.
export const useNonPoBudgetPreview = (lines, categoryId) => {
  const centers = useMemo(() => {
    const sums = new Map()
    ;(lines || []).forEach((l) => {
      if (!l.centerId) return
      sums.set(String(l.centerId), (sums.get(String(l.centerId)) || 0) + (Number(l.amount) || 0))
    })
    return Array.from(sums, ([centerId, thisPayment]) => ({ centerId, thisPayment }))
  }, [lines])
  const key = JSON.stringify({ categoryId, centers })
  const debouncedKey = useDebouncedValue(key, 400)
  const args = useMemo(() => ({ fiscalYear, fiscalMonth, ...JSON.parse(debouncedKey) }), [debouncedKey])
  const skip = !args.categoryId || args.centers.length === 0
  const { data, isFetching, isError } = useGetNonPoBudgetPreviewQuery(args, { skip })
  const byCenter = useMemo(() => new Map((skip ? [] : data || []).map((p) => [String(p.centerId), p])), [data, skip])
  return { centers, byCenter, pending: isFetching || key !== debouncedKey, isError }
}

const CenterBudget = ({ preview }) => {
  if (preview.checkStatus !== "BUDGET_FOUND" && preview.checkStatus !== "INSUFFICIENT_BUDGET") {
    return (
      <span className="prc-field-error">
        {CONFIG_MESSAGES[preview.checkStatus] || "Budget is not set up for this center."} Ask Procurement/Admin to set it up before submitting.
      </span>
    )
  }
  return (
    <>
      <div className="prc-kvs">
        <div>
          <span>Remaining budget</span>
          <b>{formatVND(preview.remaining)}</b>
        </div>
        <div>
          <span>This payment</span>
          <b>{formatVND(preview.thisPayment)}</b>
        </div>
        <div>
          <span>Remaining after submit</span>
          <b>{formatVND(preview.remainingAfterPreview)}</b>
        </div>
      </div>
      {preview.checkStatus === "INSUFFICIENT_BUDGET" ? (
        <span className="prc-field-error">Exceeds the remaining budget — this payment cannot be submitted.</span>
      ) : (
        <span className="prc-field-ok">Within budget</span>
      )}
    </>
  )
}

// Live remaining-budget check for a non-PO payment, one block per center the
// lines spend from. Reports each center's state up so Submit can be blocked.
const NonPoBudgetCheck = ({ lines, categoryId, onStatus }) => {
  const { centers, byCenter, pending, isError } = useNonPoBudgetPreview(lines, categoryId)

  useEffect(() => {
    centers.forEach(({ centerId }) => {
      const p = byCenter.get(centerId)
      onStatus(centerId, {
        loading: pending,
        missing: !pending && (isError || !p || (p.checkStatus !== "BUDGET_FOUND" && p.checkStatus !== "INSUFFICIENT_BUDGET")),
        exceeds: p?.checkStatus === "INSUFFICIENT_BUDGET",
      })
    })
  }, [centers, byCenter, pending, isError, onStatus])

  if (!categoryId) return null
  return centers.map(({ centerId }) => {
    const p = byCenter.get(centerId)
    return (
      <div key={centerId} style={{ marginTop: 8 }}>
        <div className="prc-label">{p?.centerName || `Center #${centerId}`}</div>
        {pending ? (
          <span className="prc-sub">Checking budget…</span>
        ) : !p ? (
          <span className="prc-field-error">Couldn&apos;t check the budget for this center. Please try again.</span>
        ) : (
          <CenterBudget preview={p} />
        )}
      </div>
    )
  })
}

export default NonPoBudgetCheck
